import React, { useState } from 'react';
import { ComprehensiveEnergyData } from '../types/energyData';
import { BatteryDetailView } from './drill-down/BatteryDetailView';
import { EVChargingDetailView } from './drill-down/EVChargingDetailView';
import { HeatPumpDetailView } from './drill-down/HeatPumpDetailView';
import { BuildingDetailView } from './drill-down/BuildingDetailView';
import { GridDetailView } from './drill-down/GridDetailView';
import { EnergyDetailView } from './drill-down/EnergyDetailView';
import { useTranslation } from 'react-i18next';

interface EnhancedEnergyDashboardProps {
  data: ComprehensiveEnergyData;
  energyDataService: any;
}

export const EnhancedEnergyDashboard: React.FC<EnhancedEnergyDashboardProps> = ({ data, energyDataService }) => {
  const { t } = useTranslation();
  const [selectedView, setSelectedView] = useState<string | null>(null);

  const dashboardTiles = [
    {
      id: 'energy',
      title: `📊 ${t('dashboard.energyOverview')}`,
      icon: '⚡',
      color: 'bg-blue-500',
      data: {
        primary: `${data.energy.pvProductionToday} ${t('units.kWh')}`,
        secondary: t('legends.pvProduction'),
        tertiary: `Grid: ${data.grid.netPower > 0 ? '+' : ''}${data.grid.netPower.toFixed(1)} ${t('units.kW')}`
      }
    },
    {
      id: 'battery',
      title: `🔋 ${t('dashboard.batteryStatus')}`,
      icon: '🔋',
      color: 'bg-green-500',
      data: {
        primary: `${data.battery.soc}${t('units.percent')}`,
        secondary: t('battery.batteryLevel'),
        tertiary: `${t('common.power')}: ${data.battery.chargePower > 0 ? '+' : ''}${data.battery.chargePower.toFixed(1)} ${t('units.kW')}`
      }
    },
    {
      id: 'ev',
      title: `🚗 ${t('dashboard.evCharging')}`,
      icon: '🚗',
      color: 'bg-purple-500',
      data: {
        primary: `${data.evCharging.ec1.state}`,
        secondary: t('evCharging.charger1Status'),
        tertiary: `${t('common.power')}: ${data.evCharging.ec1.power.toFixed(1)} ${t('units.kW')}`
      }
    },
    {
      id: 'heatpumps',
      title: `🌡️ ${t('dashboard.heatPumps')}`,
      icon: '🌡️',
      color: 'bg-orange-500',
      data: {
        primary: `${(data.heatPumps.hp1.power + data.heatPumps.hp2.power).toFixed(1)} ${t('units.kW')}`,
        secondary: t('heatPumps.totalHeatPumpPower'),
        tertiary: `${t('heatPumps.heatPump1')}: ${data.heatPumps.hp1.status}`
      }
    },
    {
      id: 'building',
      title: `🏠 ${t('dashboard.buildingZones')}`,
      icon: '🏠',
      color: 'bg-red-500',
      data: {
        primary: `${data.building.tz1.temperature.toFixed(1)}${t('units.celsius')}`,
        secondary: t('building.thermalZone1'),
        tertiary: `${t('common.status')}: ${data.building.tz1.seasonMode}`
      }
    },
    {
      id: 'grid',
      title: `⚡ ${t('dashboard.gridStatus')}`,
      icon: '⚡',
      color: 'bg-yellow-500',
      data: {
        primary: `${data.grid.netPower > 0 ? '+' : ''}${data.grid.netPower.toFixed(1)} ${t('units.kW')}`,
        secondary: t('grid.netPowerFlow'),
        tertiary: `${t('grid.importTariff')}: ${data.grid.importTariff > 0 ? 'High' : 'Low'}`
      }
    }
  ];

  const renderDetailView = () => {
    switch (selectedView) {
      case 'energy':
        return <EnergyDetailView data={data.energy} gridData={data.grid} energyDataService={energyDataService} />;
      case 'battery':
        return <BatteryDetailView data={data.battery} energyDataService={energyDataService} />;
      case 'ev':
        return <EVChargingDetailView data={data.evCharging} energyDataService={energyDataService} />;
      case 'heatpumps':
        return <HeatPumpDetailView data={data.heatPumps} energyDataService={energyDataService} />;
      case 'building':
        return <BuildingDetailView data={data.building} environmental={data.environmental} energyDataService={energyDataService} />;
      case 'grid':
        return <GridDetailView data={data.grid} energyDataService={energyDataService} />;
      default:
        return null;
    }
  };

  if (selectedView) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => setSelectedView(null)}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('common.back')} to Dashboard
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {dashboardTiles.find(tile => tile.id === selectedView)?.title}
              </h1>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderDetailView()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🏠 {t('dashboard.title')}</h1>
          <p className="text-gray-600">{t('dashboard.subtitle')}</p>
        </div>

        {/* Dashboard Tiles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {dashboardTiles.map((tile) => (
            <div
              key={tile.id}
              onClick={() => setSelectedView(tile.id)}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer p-6 border-l-4"
              style={{ borderLeftColor: tile.color.replace('bg-', '#') }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{tile.title}</h3>
                <span className="text-2xl">{tile.icon}</span>
              </div>
              
              <div className="space-y-2">
                <div className="text-2xl font-bold text-gray-900">{tile.data.primary}</div>
                <div className="text-sm text-gray-600">{tile.data.secondary}</div>
                <div className="text-xs text-gray-500">{tile.data.tertiary}</div>
              </div>

              <div className="mt-4 flex items-center text-sm text-blue-600">
                <span>{t('common.viewDetails')}</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats Bar */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quickStats')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{data.energy.pvProductionToday}</div>
              <div className="text-sm text-gray-600">PV {t('common.today')} ({t('units.kWh')})</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{data.battery.soc}{t('units.percent')}</div>
              <div className="text-sm text-gray-600">{t('battery.batteryLevel')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{data.evCharging.ec1.power.toFixed(1)}</div>
              <div className="text-sm text-gray-600">EV {t('common.power')} ({t('units.kW')})</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{(data.heatPumps.hp1.power + data.heatPumps.hp2.power).toFixed(1)}</div>
              <div className="text-sm text-gray-600">HP {t('common.power')} ({t('units.kW')})</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{data.building.tz1.temperature.toFixed(1)}{t('units.celsius')}</div>
              <div className="text-sm text-gray-600">{t('building.thermalZone1')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{data.environmental.ambientTemp.toFixed(1)}{t('units.celsius')}</div>
              <div className="text-sm text-gray-600">{t('building.ambient')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
